import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Account, TierExitPolicyConfig } from '../types';
import apiService from '../services/api';
import {
  DEFAULT_CHOP_EXIT_POLICY,
  DEFAULT_DIRECTIONAL_EXIT_POLICY,
  DEFAULT_TIER1_ALLOC,
  DEFAULT_TIER6_ALLOC,
  findTier1MacroTier,
  findTier6OptionsTier,
  previewDeployableCapital,
} from '../constants/tierPresets';
import { accountDetailPath } from '../utils/appRoutes';
import TierSpectrumExplainer from './portfolio/TierSpectrumExplainer';

interface Props {
  account: Account;
  onSaved: (account: Account) => void;
  onNotify: (message: string, type: 'success' | 'error') => void;
}

const pct = (n: number) => `${(n * 100).toFixed(1)}%`;
const usd = (n: number) =>
  n.toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

const AccountPortfolioSetup: React.FC<Props> = ({ account, onSaved, onNotify }) => {
  const [saving, setSaving] = useState(false);
  const [tier1Enabled, setTier1Enabled] = useState(false);
  const [tier6Enabled, setTier6Enabled] = useState(false);
  const [tier1Alloc, setTier1Alloc] = useState(DEFAULT_TIER1_ALLOC);
  const [tier6Alloc, setTier6Alloc] = useState(DEFAULT_TIER6_ALLOC);
  const [macroWeightsSource, setMacroWeightsSource] = useState<'rules' | 'ai'>('ai');
  const [tier1AutoApprove, setTier1AutoApprove] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [maxPositions, setMaxPositions] = useState<number | undefined>(3);
  const [minConfidence, setMinConfidence] = useState<number | undefined>(0.7);
  const [minDte, setMinDte] = useState(7);
  const [regimeMode, setRegimeMode] = useState<'directional' | 'range_hedge'>('directional');
  const [chopExitPolicy, setChopExitPolicy] = useState<TierExitPolicyConfig>({
    ...DEFAULT_CHOP_EXIT_POLICY,
  });
  const [directionalExitPolicy, setDirectionalExitPolicy] = useState<TierExitPolicyConfig>({
    ...DEFAULT_DIRECTIONAL_EXIT_POLICY,
  });

  const hydrate = useCallback(() => {
    const t1 = findTier1MacroTier(account.riskTiers);
    const t6 = findTier6OptionsTier(account.riskTiers);
    setTier1Enabled(t1?.enabled ?? false);
    setTier6Enabled(t6?.enabled ?? false);
    setTier1Alloc(t1?.allocationPct ?? DEFAULT_TIER1_ALLOC);
    setTier6Alloc(t6?.allocationPct ?? DEFAULT_TIER6_ALLOC);
    setMacroWeightsSource(t1?.macroWeightsSource ?? 'ai');
    setTier1AutoApprove(t1?.autoApprove ?? false);
    if (t6) {
      setMaxPositions(t6.maxPositions);
      setMinConfidence(t6.minPositionConfidence);
      setMinDte(t6.minDaysToExpiration ?? 7);
      setRegimeMode(t6.regimeMode ?? 'directional');
      if (t6.chopExitPolicy) {
        setChopExitPolicy({ ...DEFAULT_CHOP_EXIT_POLICY, ...t6.chopExitPolicy });
      }
      if (t6.directionalExitPolicy) {
        setDirectionalExitPolicy({
          ...DEFAULT_DIRECTIONAL_EXIT_POLICY,
          ...t6.directionalExitPolicy,
          roll: {
            ...DEFAULT_DIRECTIONAL_EXIT_POLICY.roll,
            ...t6.directionalExitPolicy.roll,
          },
        });
      }
    }
  }, [account]);

  useEffect(() => {
    hydrate();
  }, [hydrate]);

  const bothEnabled = tier1Enabled && tier6Enabled;
  const activeAllocSum = useMemo(() => {
    let s = 0;
    if (tier1Enabled) s += tier1Alloc;
    if (tier6Enabled) s += tier6Alloc;
    return s;
  }, [tier1Enabled, tier6Enabled, tier1Alloc, tier6Alloc]);

  const setTier1PctDisplay = (displayPct: number) => {
    const frac = Math.min(100, Math.max(0, displayPct)) / 100;
    setTier1Alloc(frac);
    if (tier1Enabled && tier6Enabled) {
      setTier6Alloc(Math.round((1 - frac) * 1000) / 1000);
    }
  };

  const setTier6PctDisplay = (displayPct: number) => {
    const frac = Math.min(100, Math.max(0, displayPct)) / 100;
    setTier6Alloc(frac);
    if (tier1Enabled && tier6Enabled) {
      setTier1Alloc(Math.round((1 - frac) * 1000) / 1000);
    }
  };

  const save = async () => {
    if (bothEnabled && Math.abs(tier1Alloc + tier6Alloc - 1) > 0.001) {
      onNotify('When both strategies are enabled, allocations must total 100%.', 'error');
      return;
    }
    if (activeAllocSum > 1.001) {
      onNotify('Enabled allocations cannot exceed 100% of account NAV.', 'error');
      return;
    }
    if (!tier1Enabled && !tier6Enabled) {
      onNotify('Enable at least one strategy.', 'error');
      return;
    }

    setSaving(true);
    try {
      const updated = await apiService.putPortfolioSetup(account.id, {
        tier1: {
          enabled: tier1Enabled,
          allocationPct: tier1Alloc,
          macroWeightsSource,
          autoApprove: tier1AutoApprove,
        },
        tier6: {
          enabled: tier6Enabled,
          allocationPct: tier6Alloc,
          minPositionConfidence: minConfidence,
          maxPositions,
          minDaysToExpiration: minDte,
          regimeMode,
          chopExitPolicy,
          directionalExitPolicy,
        },
      });
      onSaved(updated);
      onNotify('Portfolio setup saved.', 'success');
    } catch (e: unknown) {
      const raw = (e as { response?: { data?: { message?: string | string[] } } })
        ?.response?.data?.message;
      const msg = Array.isArray(raw)
        ? raw.join(', ')
        : raw || (e instanceof Error ? e.message : 'Save failed');
      onNotify(msg, 'error');
    } finally {
      setSaving(false);
    }
  };

  const tier1Configured = !!findTier1MacroTier(account.riskTiers);

  const deployPreview = useMemo(() => {
    const nav = account.balances?.totalValue ?? 0;
    if (nav <= 0) return null;
    return previewDeployableCapital(nav, account.maxUtilization ?? 0.5, [
      { enabled: tier1Enabled, allocationPct: tier1Alloc },
      { enabled: tier6Enabled, allocationPct: tier6Alloc },
    ]);
  }, [
    account.balances?.totalValue,
    account.maxUtilization,
    tier1Enabled,
    tier6Enabled,
    tier1Alloc,
    tier6Alloc,
  ]);

  return (
    <div className="space-y-4 rounded-lg border border-gray-200 bg-white p-4">
      <TierSpectrumExplainer />
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h4 className="text-sm font-semibold text-gray-900">Portfolio setup</h4>
          <p className="text-xs text-gray-500 mt-0.5">
            Tier % splits <strong>deployable</strong> capital (account max utilization × NAV). Set max
            utilization and per-trade caps in the account row above.
          </p>
          {deployPreview ? (
            <p className="text-xs text-gray-600 mt-1">
              At current NAV: deployable {usd(deployPreview.deployableUsd)} · reserve{' '}
              {usd(deployPreview.cashReserveUsd)}
              {tier1Enabled ? ` · Tier 1 ${usd(deployPreview.tierUsd[0] ?? 0)}` : ''}
              {tier6Enabled ? ` · Tier 6 ${usd(deployPreview.tierUsd[1] ?? 0)}` : ''}
            </p>
          ) : null}
        </div>
        <div className="flex flex-wrap gap-3">
          {tier1Enabled && tier1Configured ? (
            <Link
              to={accountDetailPath(account.id)}
              state={{ initialTab: 'tier1Portfolio' }}
              className="text-xs font-medium text-primary-600 hover:text-primary-800"
            >
              Open Tier 1 tab →
            </Link>
          ) : null}
          {tier6Enabled ? (
            <Link
              to={accountDetailPath(account.id)}
              state={{ initialTab: 'tier6Portfolio' }}
              className="text-xs font-medium text-violet-700 hover:text-violet-900"
            >
              Open Tier 6 tab →
            </Link>
          ) : null}
        </div>
      </div>

      <div className="space-y-2">
        <div className="flex h-3 w-full overflow-hidden rounded-full bg-gray-100">
          {tier1Enabled ? (
            <div
              className="bg-emerald-500 transition-all"
              style={{ width: `${tier1Alloc * 100}%` }}
              title={`Tier 1 ${pct(tier1Alloc)}`}
            />
          ) : null}
          {tier6Enabled ? (
            <div
              className="bg-violet-500 transition-all"
              style={{ width: `${tier6Alloc * 100}%` }}
              title={`Tier 6 ${pct(tier6Alloc)}`}
            />
          ) : null}
        </div>
        <p className="text-xs text-gray-600">
          {bothEnabled ? (
            <>
              <span className="text-emerald-700">Tier 1 {pct(tier1Alloc)}</span>
              {' · '}
              <span className="text-violet-700">Tier 6 {pct(tier6Alloc)}</span>
              {Math.abs(tier1Alloc + tier6Alloc - 1) < 0.001 ? (
                <span className="text-green-700"> · 100% allocated</span>
              ) : (
                <span className="text-amber-700">
                  {' '}
                  · Must sum to 100% when both enabled
                </span>
              )}
            </>
          ) : (
            <>Active allocation: {pct(activeAllocSum)} of NAV</>
          )}
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div className="rounded border border-emerald-100 bg-emerald-50/50 p-3 space-y-3">
          <label className="flex items-center gap-2 text-sm font-medium text-gray-900">
            <input
              type="checkbox"
              checked={tier1Enabled}
              onChange={(e) => setTier1Enabled(e.target.checked)}
            />
            Long-term macro (Tier 1)
          </label>
          <div>
            <label className="text-xs text-gray-600">Allocation %</label>
            <input
              type="number"
              min={0}
              max={100}
              step={0.1}
              disabled={!tier1Enabled}
              className="mt-1 w-full border rounded px-2 py-1 text-sm disabled:opacity-50"
              value={Math.round(tier1Alloc * 1000) / 10}
              onChange={(e) => setTier1PctDisplay(parseFloat(e.target.value) || 0)}
            />
          </div>
          <div>
            <label className="text-xs text-gray-600">Macro weights</label>
            <select
              disabled={!tier1Enabled}
              className="mt-1 w-full border rounded px-2 py-1 text-sm disabled:opacity-50"
              value={macroWeightsSource}
              onChange={(e) =>
                setMacroWeightsSource(e.target.value as 'rules' | 'ai')
              }
            >
              <option value="rules">Rules (cockpit)</option>
              <option value="ai">AI</option>
            </select>
          </div>
          <label className="flex items-center gap-2 text-xs text-gray-700">
            <input
              type="checkbox"
              disabled={!tier1Enabled}
              checked={tier1AutoApprove}
              onChange={(e) => setTier1AutoApprove(e.target.checked)}
            />
            Automatic execution (skip SMS approval; submit orders when proposals are created)
          </label>
        </div>

        <div className="rounded border border-violet-100 bg-violet-50/50 p-3 space-y-3">
          <label className="flex items-center gap-2 text-sm font-medium text-gray-900">
            <input
              type="checkbox"
              checked={tier6Enabled}
              onChange={(e) => setTier6Enabled(e.target.checked)}
            />
            Active options (Tier 6)
          </label>
          <div>
            <label className="text-xs text-gray-600">Allocation %</label>
            <input
              type="number"
              min={0}
              max={100}
              step={0.1}
              disabled={!tier6Enabled}
              className="mt-1 w-full border rounded px-2 py-1 text-sm disabled:opacity-50"
              value={Math.round(tier6Alloc * 1000) / 10}
              onChange={(e) => setTier6PctDisplay(parseFloat(e.target.value) || 0)}
            />
          </div>
          <button
            type="button"
            className="text-xs text-primary-600 hover:underline"
            onClick={() => setAdvancedOpen((o) => !o)}
          >
            {advancedOpen ? 'Hide' : 'Show'} advanced options settings
          </button>
        </div>
      </div>

      {advancedOpen && tier6Enabled ? (
        <div className="grid gap-3 md:grid-cols-3 text-sm border-t pt-3">
          <div>
            <label className="text-xs text-gray-600">Max positions</label>
            <input
              type="number"
              min={1}
              className="mt-1 w-full border rounded px-2 py-1"
              value={maxPositions ?? ''}
              onChange={(e) =>
                setMaxPositions(e.target.value ? parseInt(e.target.value, 10) : undefined)
              }
            />
          </div>
          <div>
            <label className="text-xs text-gray-600">Min confidence (0–100)</label>
            <input
              type="number"
              min={0}
              max={100}
              className="mt-1 w-full border rounded px-2 py-1"
              value={minConfidence != null ? minConfidence * 100 : ''}
              onChange={(e) =>
                setMinConfidence(
                  e.target.value ? parseFloat(e.target.value) / 100 : undefined,
                )
              }
            />
          </div>
          <div>
            <label className="text-xs text-gray-600">Min DTE (options)</label>
            <input
              type="number"
              min={1}
              className="mt-1 w-full border rounded px-2 py-1"
              value={minDte}
              onChange={(e) => setMinDte(parseInt(e.target.value, 10) || 7)}
            />
          </div>
          <div>
            <label className="text-xs text-gray-600">Regime mode</label>
            <select
              className="mt-1 w-full border rounded px-2 py-1"
              value={regimeMode}
              onChange={(e) =>
                setRegimeMode(e.target.value as 'directional' | 'range_hedge')
              }
            >
              <option value="directional">Directional</option>
              <option value="range_hedge">Range hedge</option>
            </select>
          </div>
          <div>
            <label className="text-xs text-gray-600">Chop take profit %</label>
            <input
              type="number"
              className="mt-1 w-full border rounded px-2 py-1"
              value={chopExitPolicy.takeProfitPct ?? ''}
              onChange={(e) =>
                setChopExitPolicy({
                  ...chopExitPolicy,
                  takeProfitPct: parseFloat(e.target.value) || 0,
                })
              }
            />
          </div>
          <div>
            <label className="text-xs text-gray-600">Directional take profit %</label>
            <input
              type="number"
              className="mt-1 w-full border rounded px-2 py-1"
              value={directionalExitPolicy.takeProfitPct ?? ''}
              onChange={(e) =>
                setDirectionalExitPolicy({
                  ...directionalExitPolicy,
                  takeProfitPct: parseFloat(e.target.value) || 0,
                })
              }
            />
          </div>
        </div>
      ) : null}

      <button
        type="button"
        disabled={saving}
        onClick={() => void save()}
        className="rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
      >
        {saving ? 'Saving…' : 'Save portfolio setup'}
      </button>
    </div>
  );
};

export default AccountPortfolioSetup;
